import React from 'react';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import InCellDivisionBlock, { InCellDivisionBlockProps } from 'components/common/general/InCellDivisionBlock';
import {
  DashboardEnrollmentPresenter,
  EnrollmentPeriod,
  EnrollmentStage,
  RfqProposalStatus,
} from 'types/enrollmentService';
import { GroupFilter } from 'types/groupService';
import { colors, Tooltip } from '@insurancemenu/imenu-components-lib';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  InfoButtonFilled,
  Clock,
  DollarCircle,
  AddCircle,
  AccountCircle,
  CheckCircle,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import ProductLinCellContent from './ProductLineCellContent';
import StageTag from '../StageTag';
import { createStyles } from './styles';

export interface InCellDivisionWrapper {
  id: string;
  inCellProps?: InCellDivisionBlockProps;
  maxWidth?: string;
  withBorder?: boolean;
  children: React.ReactNode;
}

export interface CellProps {
  effectiveDateProps?: InCellDivisionBlockProps;
  productLineProps?: InCellDivisionBlockProps;
  stagesProps?: InCellDivisionBlockProps;
}

export type ExtendedDashboardEnrollmentType = Pick<
  EnrollmentPeriod,
  'effectiveDate' | 'productLineIds' | 'id' | 'proposals'
> & {
  stage: EnrollmentPeriod['stage'] | 'DORMANT';
};

export const InCellDivisionWrapper: React.FC<InCellDivisionWrapper> = (props) => {
  return props.inCellProps ? (
    <InCellDivisionBlock {...{ ...props.inCellProps, withBorder: props.withBorder }} key={props.id}>
      {props.children}
    </InCellDivisionBlock>
  ) : (
    <InCellDivisionBlock withBorder={props.withBorder || false} maxWidth={props.maxWidth} key={props.id}>
      {props.children}
    </InCellDivisionBlock>
  );
};

const ProposalStatusDetails: Record<RfqProposalStatus, { icon: React.ReactNode; title: string }> = Object.freeze({
  [RfqProposalStatus.INITIATED]: {
    icon: <InfoButtonFilled width="2.2rem" height="2.2rem" color={colors.green[600]} />,
    title: 'Initiated',
  },
  [RfqProposalStatus.PENDING_UW]: {
    icon: <Clock width="2.2rem" height="2.2rem" color={colors.orange[600]} />,
    title: 'Pending UW',
  },
  [RfqProposalStatus.RATED]: {
    icon: <DollarCircle width="2.2rem" height="2.2rem" color={colors.yellow[600]} />,
    title: 'Rated',
  },
  [RfqProposalStatus.PENDING_RELEASE]: {
    icon: <Clock width="2.2rem" height="2.2rem" color={colors.blue[600]} />,
    title: 'Pending Release',
  },
  [RfqProposalStatus.ACTION_REQUIRED]: {
    icon: <AddCircle width="2.2rem" height="2.2rem" color={colors.orange[600]} />,
    title: 'Action Required',
  },
  [RfqProposalStatus.RESPONSE_PENDING]: {
    icon: <AccountCircle width="2.2rem" height="2.2rem" color={colors.purple[600]} />,
    title: 'Pending Response',
  },
  [RfqProposalStatus.RESPONSE_READY]: {
    icon: <CheckCircle width="2.2rem" height="2.2rem" color={colors.blue[600]} />,
    title: 'Response Ready',
  },
  [RfqProposalStatus.RELEASED]: {
    icon: null,
    title: 'Released',
  },
  [RfqProposalStatus.EMPLOYEES_ADDED]: {
    icon: <AccountCircle width="2.2rem" height="2.2rem" color={colors.green[600]} />,
    title: 'Browsing Ready',
  },
});

// Common Dashboard Cells content
export const createCommonDashboardCells = (
  enrollmentPeriods: DashboardEnrollmentPresenter[],
  filter: GroupFilter | null,
  cellProps?: CellProps,
): { [key: string]: React.ReactNode } => {
  const classes = createStyles();

  const effectiveDatesCellContent = enrollmentPeriods.map((enrollmentPeriod, enrollmentIndex) => {
    const ProposalsStatuses = enrollmentPeriod.proposals
      .filter((proposal) => proposal?.status && proposal.status !== RfqProposalStatus.RELEASED)
      .sort((a, b) => {
        const statusOrder: Record<RfqProposalStatus, number> = {
          [RfqProposalStatus.RESPONSE_READY]: 0,
          [RfqProposalStatus.RESPONSE_PENDING]: 1,
          [RfqProposalStatus.ACTION_REQUIRED]: 2,
          [RfqProposalStatus.PENDING_RELEASE]: 3,
          [RfqProposalStatus.RATED]: 4,
          [RfqProposalStatus.PENDING_UW]: 5,
          [RfqProposalStatus.EMPLOYEES_ADDED]: 5,
          [RfqProposalStatus.INITIATED]: 6,
          [RfqProposalStatus.RELEASED]: 7, // will be filtered out
        };
        return a.status && b.status ? statusOrder[a.status] - statusOrder[b.status] : 0;
      })
      .map((proposal, index) => {
        const status = proposal.status;
        return (
          // Ensure proposal.status is defined to avoid TypeScript error
          // eslint-disable-next-line react/no-array-index-key
          <div className={classes.statusTooltipWrapper} key={index}>
            <Tooltip alignment={AlignmentTypes.RIGHT_CENTER} label={status ? ProposalStatusDetails[status]?.title : ''}>
              {status && ProposalStatusDetails[status].icon}
            </Tooltip>
          </div>
        );
      });

    const FirstStatuses = ProposalsStatuses.slice(0, 2); // Get the first two components
    const remainingCount = Math.max(0, ProposalsStatuses.length - 2); // Calculate the number of remaining components

    const id = enrollmentPeriod.effectiveDate
      ? `effectiveDate-${enrollmentPeriod.effectiveDate}`
      : enrollmentIndex.toString();

    return enrollmentPeriod.effectiveDate ? (
      <InCellDivisionWrapper key={id} id={id} withBorder={cellProps?.effectiveDateProps?.withBorder || false}>
        <div className={classes.effectiveDateWrapper}>
          {dateFormatters.normDateToShortAmericanDate(enrollmentPeriod.effectiveDate)}
          <div className={classes.effectiveDateStatusesWrapper}>
            <div className={classes.statuses}>{FirstStatuses}</div>
            {remainingCount > 0 && <span>{`+${remainingCount}`}</span>}
          </div>
        </div>
      </InCellDivisionWrapper>
    ) : null;
  });

  const productLinesCellContent = enrollmentPeriods.map((enrollmentPeriod, enrollmentPeriodIndex) => {
    const id = enrollmentPeriod.effectiveDate
      ? `productLines-${enrollmentPeriod.effectiveDate}`
      : enrollmentPeriodIndex.toString();
    const isGroupWithMultiplePeriods = enrollmentPeriods.length > 1;

    return (
      <ProductLinCellContent
        key={id}
        id={id}
        isGroupWithMultiplePeriods={isGroupWithMultiplePeriods}
        enrollmentPeriod={enrollmentPeriod}
        cellProps={cellProps?.productLineProps}
      />
    );
  });

  const renderDefaultStage = (): React.ReactNode => {
    switch (filter) {
      case GroupFilter.NEW_LEAD:
      case GroupFilter.ALL:
        return <StageTag stage={EnrollmentStage.NEW_LEAD} />;
      case GroupFilter.SOLD:
        return <StageTag stage={EnrollmentStage.SOLD} />;
      case GroupFilter.ARCHIVED:
        return <StageTag stage={EnrollmentStage.ARCHIVED} />;
      case GroupFilter.DORMANT:
        return <StageTag stage="DORMANT" />;

      default:
        return null;
    }
  };

  const stagesCellContent =
    enrollmentPeriods.length > 0
      ? enrollmentPeriods.map((enrollmentPeriod, index) => {
          const id = enrollmentPeriod.effectiveDate ? `stages-${enrollmentPeriod.effectiveDate}` : index.toString();
          return (
            <InCellDivisionWrapper
              inCellProps={cellProps?.stagesProps}
              withBorder={cellProps?.productLineProps?.withBorder || false}
              key={enrollmentPeriod.effectiveDate ? `stages-${enrollmentPeriod.effectiveDate}` : index}
              id={id}
            >
              <StageTag stage={enrollmentPeriod.stage} />
            </InCellDivisionWrapper>
          );
        })
      : renderDefaultStage();

  return {
    effectiveDates: effectiveDatesCellContent,
    productLines: productLinesCellContent,
    stages: stagesCellContent,
  };
};
